import { useCallback, useEffect, useState } from "react";
import { History, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { listReportVersions, getReportVersion, deleteReportVersion } from "@/lib/reports";

/** LOT 40 — Barre « Historique des versions » + Corbeille, partagée par les rapports procéduraux mono-session. */
export default function ReportVersionsBar({ dossierId, kind, currentSessionId, onLoad, refreshKey = 0, disabled }) {
    const [versions, setVersions] = useState([]);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(() => {
        listReportVersions(dossierId, kind).then((r) => setVersions(r || [])).catch(() => {});
    }, [dossierId, kind]);

    useEffect(() => { refresh(); }, [refresh, refreshKey]);

    if (versions.length === 0) return null;

    async function pick(sid) {
        if (!sid || sid === currentSessionId) return;
        try { onLoad(await getReportVersion(dossierId, kind, sid)); }
        catch (e) { toast.error("Chargement de la version impossible", { description: e.message }); }
    }

    async function del() {
        if (!currentSessionId || !window.confirm("Supprimer cette version du rapport ? Cette action est définitive.")) return;
        setBusy(true);
        try {
            await deleteReportVersion(dossierId, kind, currentSessionId);
            const rest = versions.filter((v) => v.session_id !== currentSessionId);
            setVersions(rest);
            if (rest[0]) onLoad(await getReportVersion(dossierId, kind, rest[0].session_id));
            else onLoad(null);
            toast.success("Version supprimée");
        } catch (e) { toast.error("Suppression impossible", { description: e.message }); }
        finally { setBusy(false); }
    }

    const label = (v) => `${v.titre || "Version"} · ${new Date(v.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`;

    return (
        <div data-testid={`${kind}-versions-bar`} className="ml-auto flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
            <Select value={currentSessionId || ""} onValueChange={pick} disabled={disabled || busy}>
                <SelectTrigger data-testid={`${kind}-versions-select`} className="h-8 w-[264px] max-w-[60vw] bg-background text-xs"><SelectValue placeholder="Historique des versions…" /></SelectTrigger>
                <SelectContent>{versions.map((v) => <SelectItem key={v.session_id} value={v.session_id} className="text-xs">{label(v)}</SelectItem>)}</SelectContent>
            </Select>
            <button type="button" data-testid={`${kind}-version-delete`} onClick={del} disabled={disabled || busy || !currentSessionId}
                aria-label="Supprimer la version (corbeille)"
                className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </button>
        </div>
    );
}
